import { useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ChevronDown, RefreshCw, Settings2 } from "lucide-react";
import { printerDisplayName, MEDIA_LABEL } from "@/lib/printerMap";

// Compact "Prints to <friendly printer name>" line with an out-of-the-way override.
// The override lists ONLY printers that hold this stock, so the wrong layout can't be
// aimed at the wrong printer from here. Printers left out are DISCLOSED with the reason,
// never quietly dropped. Never shows a raw serial. Feed it usePrinterTarget(media).
export function PrintDestination({ target, devices, eligible = [], media, overrideUid, setOverrideUid, loaded, reload, testid = "print-destination" }) {
  const unmapped = loaded && devices.length > 0 && !target;   // detected, but none holds this stock
  const hidden = Math.max(0, (devices || []).length - eligible.length);
  const [showPicker, setShowPicker] = useState(false);
  const stockLabel = MEDIA_LABEL[media] || "this stock";
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <Printer className="w-4 h-4 text-slate-400 shrink-0" />
        {!loaded ? (
          <span className="text-slate-400">Finding printer…</span>
        ) : target ? (
          <span className="text-slate-700 min-w-0 truncate">Prints to <b className="text-slate-900" data-testid={`${testid}-name`}>{printerDisplayName(target)}</b>{target.matchedBy === "spare" ? <span className="text-sky-700"> · spare {stockLabel} unit (your default one isn't connected)</span> : null}</span>
        ) : unmapped ? (
          <span className="text-[#B01E28] font-semibold min-w-0" data-testid={`${testid}-unmapped`}>No connected printer holds {stockLabel} — set one in Printer Setup. Nothing prints until then.</span>
        ) : (
          <span className="text-amber-700 min-w-0">No printer detected — start Zebra Browser Print.</span>
        )}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {!target && loaded && !unmapped && reload && (
            <button type="button" data-testid={`${testid}-recheck`} onClick={() => reload(true)} className="text-[11px] text-slate-500 hover:text-[#B01E28] inline-flex items-center gap-0.5"><RefreshCw className="w-3 h-3" />Recheck</button>
          )}
          {loaded && eligible.length > 1 && (
            <button type="button" data-testid={`${testid}-change`} onClick={() => setShowPicker((s) => !s)}
              className="text-[11px] text-[#B01E28] hover:underline inline-flex items-center gap-0.5">
              Different {stockLabel} printer <ChevronDown className={`w-3 h-3 transition-transform ${showPicker ? "rotate-180" : ""}`} />
            </button>
          )}
          {loaded && unmapped && (
            <Link to="/printer-settings" data-testid={`${testid}-setup`} className="text-[11px] text-slate-500 hover:text-[#B01E28] inline-flex items-center gap-0.5"><Settings2 className="w-3 h-3" />Setup</Link>
          )}
        </div>
      </div>
      {showPicker && eligible.length > 1 && (
        <select data-testid={`${testid}-select`} value={overrideUid || (target?.uid || "")}
          onChange={(e) => setOverrideUid(e.target.value || null)}
          className="mt-2 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm">
          {eligible.map((d) => <option key={d.uid} value={d.uid}>{printerDisplayName(d)}</option>)}
        </select>
      )}
      {loaded && hidden > 0 && (
        <div data-testid={`${testid}-excluded`} className="text-[11px] text-slate-500 mt-1">
          {hidden} other connected printer{hidden === 1 ? "" : "s"} can't print this — {hidden === 1 ? "it doesn't" : "they don't"} hold {stockLabel}. Change the media above to print on other stock.
        </div>
      )}
    </div>
  );
}
